/**
 * Application Constants
 * CCH Care - Version 1.0
 * Must stay in sync with cch-care-api/config/constants.php
 * and with the CSS variables defined in src/index.css
 *
 * Last Modified: 2026-04-21
 *
* Changes (2026-04-21):
 * - Added APP_VERSION export.
 * - Added ALERT_BADGES + ROLE_BADGES exports — token-backed objects
 *   consumed by ResidentCard, ResidentsDirectoryPage, StaffManagement.
 *   Dedupes two copies of ALERT_BADGE_COLORS and replaces ROLE_COLORS
 *   hex-concat pattern with dedicated alpha tokens. 
* Changes (2026-04-20) — Palette expansion for unified tokenization:
 * - Added auth palette (15 tokens) for LoginPage, ForgotPassword,
 *   ResetPassword. Auth pages stay navy in both themes — it's a brand
 *   surface, not a workspace surface.
 * - Added alert badge palette (9 bg + 9 text pairs) for
 *   ResidentsDirectoryPage. Replaces the hardcoded ALERT_BADGE_COLORS
 *   object in that file.
 * - Added role palette (4 solid + 4 alpha) for StaffManagement.
 *   Intentionally decoupled from workspace primary/accent/warning even
 *   where values currently match — role identity is a separate concept.
 * - Added credential amber palette (5 tokens) for one-time-secret
 *   visual treatment. Used by StaffManagement's CredentialsCard and
 *   reset-password banner; reserved for password reset flows next.
 */

export const APP_NAME = 'CCH Care';
export const APP_VERSION = '1.0.0';

// Session timeout (HIPAA: 15 minutes)
export const SESSION_TIMEOUT_MS = 15 * 60 * 1000;
export const SESSION_WARNING_MS = 2 * 60 * 1000; // Warn 2 min before timeout

// Session timeout test
/*export const SESSION_TIMEOUT_MS = 30 * 1000; // timeout in 30 seconds.
export const SESSION_WARNING_MS = 10 * 1000; // warn 10 seconds*/

// Assistance levels
export const ASSIST_LEVELS = [
  { code: 'I', label: 'Independent', color: '#2ECC71', bg: '#E8F8F0' },
  { code: 'A', label: 'Assisted',    color: '#1B6B93', bg: '#E8F4F8' },
  { code: 'D', label: 'Dependent',   color: '#E67E22', bg: '#FEF5E7' },
  { code: 'R', label: 'Refused',     color: '#E74C3C', bg: '#FDEDEC' },
];
// Assistance level lookup — convert single-letter codes (I/A/D/R) to
// user-facing labels. Use on review surfaces so bare codes never leak
// to the user. Falls through to the raw code if unrecognized, so future
// codes (e.g. S = Supervision) won't render blank.
export const assistLevelLabel = (code) =>
  ASSIST_LEVELS.find(l => l.code === code)?.label ?? code;


// Hygiene task types
export const HYGIENE_TASKS = [
  { key: 'oral_care',     label: 'Oral Care',     icon: '🪥' },
  { key: 'bathing',       label: 'Bathing',        icon: '🚿' },
  { key: 'perineal_care', label: 'Perineal Care',  icon: '🧴' },
  { key: 'grooming',      label: 'Grooming',       icon: '✂️' },
  { key: 'dressing',      label: 'Dressing',       icon: '👕' },
  { key: 'nail_care',     label: 'Nail Care',      icon: '💅' },
];

// Meal types
export const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];

// Fluid types
export const FLUID_TYPES = ['Water', 'Juice', 'Coffee', 'Milk', 'Thickened'];
export const FLUID_PRESETS = [60, 120, 240, 360]; // mL

// Repositioning positions
export const POSITIONS = ['left', 'right', 'back', 'elevated', 'chair'];

// Bowel sizes
export const BOWEL_SIZES = [
  { code: 'S', label: 'Small'  },
  { code: 'M', label: 'Medium' },
  { code: 'L', label: 'Large'  },
];

// Urination types
export const URINE_TYPES = [
  { code: 'voided',      label: 'Voided',      icon: '🚽' },
  { code: 'incontinent', label: 'Incontinent', icon: '⚠️' },
  { code: 'catheter',    label: 'Catheter',    icon: '🏥' },
];

// Safety checklist categories
export const SAFETY_CATEGORIES = ['Patient Safety', 'Environment', 'PPE'];

// ADL record statuses
export const ADL_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETED:   'completed',
  AMENDED:     'amended',
};

// Alert severities
export const ALERT_SEVERITY = {
  INFO:     'info',
  WARNING:  'warning',
  CRITICAL: 'critical',
};

/**
 * Color tokens — CSS variable references.
 *
 * Light mode values (defined in :root in index.css):
 *   primary       #1B6B93    primaryLight  #E8F4F8    primaryDark  #12516F
 *   accent        #2ECC71    accentLight   #E8F8F0
 *   warning       #E67E22    warningLight  #FEF5E7
 *   danger        #E74C3C    dangerLight   #FDEDEC
 *   text          #1A2332    textMuted     #6B7B8D    textLight    #94A3B8
 *   bg            #F0F2F5    surface       #FFFFFF    border       #E2E8F0
 *
 * Dark mode overrides in [data-theme="dark"] in index.css.
 *
 * ALPHA VARIANTS — use these instead of COLORS.x + '44' hex concat:
 *   COLORS.primaryAlpha  COLORS.accentAlpha  COLORS.dangerAlpha
 *   COLORS.warningAlpha  COLORS.borderAlpha
 *
 * AUTH palette — for LoginPage / ForgotPassword / ResetPassword.
 *   Same values in both themes (auth is always navy). Examples:
 *   COLORS.authBg         — page background
 *   COLORS.authSurface    — card bg
 *   COLORS.authPrimary    — primary button bg (blue)
 *   COLORS.authBrandBg    — logo square bg (teal, matches workspace primary)
 *   COLORS.authText       — pure white body text
 *   COLORS.authTextLabel  — form labels (light gray)
 *   COLORS.authTextMuted  — body muted text
 *   COLORS.authTextHelper — smallest helper text
 *   COLORS.authLink       — inline link color
 *   COLORS.authAccent     — step subtitles, highlighted inline text
 *   COLORS.authErrorText  — error banner text
 *   COLORS.authWarningText — session-expired banner text
 *
 * ALERT palette — for ResidentsDirectoryPage badges.
 *   Each alert type has { bg, text }. Access as:
 *   COLORS.alertFallRiskBg / COLORS.alertFallRiskText
 *   Types: fallRisk, dnr, diet, diabetic, isolation, elopement,
 *          aspiration, allergy, custom.
 *
 * ROLE palette — for StaffManagement avatars and role pills.
 *   Decoupled from workspace palette. Each role has a solid color
 *   and an alpha variant (for avatar bg / pill bg tinting).
 *   Roles: cna, careSupervisor, careAdmin, nurse.
 *   Example: COLORS.roleCna / COLORS.roleCnaAlpha
 *
 * CREDENTIAL palette — for "one-time secret, read now" UX pattern.
 *   Distinct from warning palette — semantic is "ephemeral sensitive
 *   data" not "caution state". 5 tokens for bg/borders/text layers.
 */
export const COLORS = {
  // Brand
  primary:      'var(--color-primary)',
  primaryLight: 'var(--color-primary-light)',
  primaryDark:  'var(--color-primary-dark)',

  // Status
  accent:       'var(--color-accent)',
  accentLight:  'var(--color-accent-light)',
  warning:      'var(--color-warning)',
  warningLight: 'var(--color-warning-light)',
  danger:       'var(--color-danger)',
  dangerLight:  'var(--color-danger-light)',

  // Typography
  text:         'var(--color-text)',
  textMuted:    'var(--color-text-muted)',
  textLight:    'var(--color-text-light)',

  // Surfaces
  bg:           'var(--color-bg)',
  surface:      'var(--color-surface)',
  border:       'var(--color-border)',

  // Alpha variants — use instead of COLORS.x + '44'
  primaryAlpha: 'var(--color-primary-alpha)',
  accentAlpha:  'var(--color-accent-alpha)',
  dangerAlpha:  'var(--color-danger-alpha)',
  warningAlpha: 'var(--color-warning-alpha)',
  borderAlpha:  'var(--color-border-alpha)',

  // Auth palette — LoginPage, ForgotPassword, ResetPassword
  authBg:             'var(--color-auth-bg)',
  authSurface:        'var(--color-auth-surface)',
  authSurfaceBorder:  'var(--color-auth-surface-border)',
  authInputBg:        'var(--color-auth-input-bg)',
  authInputBorder:    'var(--color-auth-input-border)',
  authPrimary:        'var(--color-auth-primary)',
  authBrandBg:        'var(--color-auth-brand-bg)',
  authText:           'var(--color-auth-text)',
  authTextLabel:      'var(--color-auth-text-label)',
  authTextMuted:      'var(--color-auth-text-muted)',
  authTextHelper:     'var(--color-auth-text-helper)',
  authLink:           'var(--color-auth-link)',
  authAccent:         'var(--color-auth-accent)',
  authErrorText:      'var(--color-auth-error-text)',
  authWarningText:    'var(--color-auth-warning-text)',
  authErrorBg:        'var(--color-auth-error-bg)',
  authErrorBorder:    'var(--color-auth-error-border)',
  authWarningBg:      'var(--color-auth-warning-bg)',
  authWarningBorder:  'var(--color-auth-warning-border)',
  authSuccessBg:      'var(--color-auth-success-bg)',
  authSuccessBorder:  'var(--color-auth-success-border)',

  // Alert badge palette — ResidentsDirectoryPage
  alertFallRiskBg:    'var(--color-alert-fall-risk-bg)',
  alertFallRiskText:  'var(--color-alert-fall-risk-text)',
  alertDnrBg:         'var(--color-alert-dnr-bg)',
  alertDnrText:       'var(--color-alert-dnr-text)',
  alertDietBg:        'var(--color-alert-diet-bg)',
  alertDietText:      'var(--color-alert-diet-text)',
  alertDiabeticBg:    'var(--color-alert-diabetic-bg)',
  alertDiabeticText:  'var(--color-alert-diabetic-text)',
  alertIsolationBg:   'var(--color-alert-isolation-bg)',
  alertIsolationText: 'var(--color-alert-isolation-text)',
  alertElopementBg:   'var(--color-alert-elopement-bg)',
  alertElopementText: 'var(--color-alert-elopement-text)',
  alertAspirationBg:  'var(--color-alert-aspiration-bg)',
  alertAspirationText:'var(--color-alert-aspiration-text)',
  alertAllergyBg:     'var(--color-alert-allergy-bg)',
  alertAllergyText:   'var(--color-alert-allergy-text)',
  alertCustomBg:      'var(--color-alert-custom-bg)',
  alertCustomText:    'var(--color-alert-custom-text)',

  // Role palette — StaffManagement. Decoupled from workspace tokens.
  roleCna:                   'var(--color-role-cna)',
  roleCnaAlpha:              'var(--color-role-cna-alpha)',
  roleCareSupervisor:        'var(--color-role-care-supervisor)',
  roleCareSupervisorAlpha:   'var(--color-role-care-supervisor-alpha)',
  roleCareAdmin:             'var(--color-role-care-admin)',
  roleCareAdminAlpha:        'var(--color-role-care-admin-alpha)',
  roleNurse:                 'var(--color-role-nurse)',
  roleNurseAlpha:            'var(--color-role-nurse-alpha)',

  // Credential palette — one-time-secret visual treatment
  credentialBg:           'var(--color-credential-bg)',
  credentialBorder:       'var(--color-credential-border)',
  credentialInnerBorder:  'var(--color-credential-inner-border)',
  credentialText:         'var(--color-credential-text)',
  credentialTextHelper:   'var(--color-credential-text-helper)',
};

/**
 * ALERT_BADGES — resident alert badge config keyed by alert_type.
 * Consumed by ResidentCard.jsx and ResidentsDirectoryPage.jsx.
 * `label` is a fallback when `alert.alert_label` isn't provided by the API.
 */
export const ALERT_BADGES = {
  fall_risk:        { bg: COLORS.alertFallRiskBg,   text: COLORS.alertFallRiskText,   label: 'Fall Risk'  },
  dnr:              { bg: COLORS.alertDnrBg,        text: COLORS.alertDnrText,        label: 'DNR'        },
  diet_restriction: { bg: COLORS.alertDietBg,       text: COLORS.alertDietText,       label: 'Diet'       },
  diabetic:         { bg: COLORS.alertDiabeticBg,   text: COLORS.alertDiabeticText,   label: 'Diabetic'   },
  isolation:        { bg: COLORS.alertIsolationBg,  text: COLORS.alertIsolationText,  label: 'Isolation'  },
  elopement_risk:   { bg: COLORS.alertElopementBg,  text: COLORS.alertElopementText,  label: 'Elopement'  },
  aspiration_risk:  { bg: COLORS.alertAspirationBg, text: COLORS.alertAspirationText, label: 'Aspiration' },
  allergy:          { bg: COLORS.alertAllergyBg,    text: COLORS.alertAllergyText,    label: 'Allergy'    },
  custom:           { bg: COLORS.alertCustomBg,     text: COLORS.alertCustomText,     label: 'Alert'      },
};

/**
 * ROLE_BADGES — staff role visual treatment keyed by role name.
 * `color` = solid (text, avatar text). `alpha` = tinted bg (avatar/pill bg).
 * Replaces the ROLE_COLORS + hex-concat alpha pattern in StaffManagement.
 */
export const ROLE_BADGES = {
  'CNA':             { color: COLORS.roleCna,            alpha: COLORS.roleCnaAlpha            },
  'Care Supervisor': { color: COLORS.roleCareSupervisor, alpha: COLORS.roleCareSupervisorAlpha },
  'Care Admin':      { color: COLORS.roleCareAdmin,      alpha: COLORS.roleCareAdminAlpha      },
  'Nurse':           { color: COLORS.roleNurse,          alpha: COLORS.roleNurseAlpha          },
};